/**
 * useFaceLandmarker Hook
 * ======================
 * Loads and manages the Face Landmarker model.
 * Runs entirely in-browser via WebAssembly - no API calls.
 */

import { useEffect, useRef, useState, useCallback } from 'react';

const WASM_CDN = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/wasm';
const MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

export function useFaceLandmarker() {
  const [isLoading, setIsLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);
  const [error, setError] = useState(null);
  const faceLandmarkerRef = useRef(null);
  const lastTimestampRef = useRef(0);
  const initAttempted = useRef(false);
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);

  useEffect(() => {
    if (initAttempted.current) return;
    initAttempted.current = true;

    async function init() {
      try {
        setLoadProgress(20);

        // Dynamic import to avoid bundling WASM in main chunk
        const { FaceLandmarker, FilesetResolver } = await import('@mediapipe/tasks-vision');
        setLoadProgress(40);

        const vision = await FilesetResolver.forVisionTasks(WASM_CDN);
        setLoadProgress(60);

        // Use CPU delegate - more reliable across browsers
        // GPU delegate causes WebGL activeTexture errors on many machines
        const faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: MODEL_URL,
            delegate: 'CPU',
          },
          runningMode: 'VIDEO',
          numFaces: 1,
          outputFaceBlendshapes: false,
          outputFacialTransformationMatrixes: false,
        });

        faceLandmarkerRef.current = faceLandmarker;
        setLoadProgress(100);
        setIsLoading(false);
      } catch (err) {
        console.error('Failed to load face landmarker:', err);
        setError(err.message || 'Failed to load face detection model');
        setIsLoading(false);
      }
    }

    init();

    return () => {
      if (faceLandmarkerRef.current) {
        faceLandmarkerRef.current.close();
        faceLandmarkerRef.current = null;
      }
    };
  }, []);

  /**
   * Detect face landmarks in a video frame.
   * Uses a hidden canvas as intermediary to avoid WebGL texture upload issues.
   * @param {HTMLVideoElement} video - Video element to analyze
   * @param {number} timestamp - Current timestamp (performance.now())
   * @returns {Object|null} Detection result with faceLandmarks array
   */
  const detect = useCallback((video, timestamp) => {
    if (!faceLandmarkerRef.current) return null;
    // Video must have valid dimensions before detection
    if (!video.videoWidth || !video.videoHeight) return null;
    // Face landmarker requires strictly increasing timestamps
    if (timestamp <= lastTimestampRef.current) {
      timestamp = lastTimestampRef.current + 1;
    }
    lastTimestampRef.current = timestamp;

    try {
      // Draw video frame to a 2D canvas first.
      // This avoids the WebGL texture upload path that fails on machines
      // without GPU/WebGL support. The 2D canvas context always works.
      if (!canvasRef.current) {
        canvasRef.current = document.createElement('canvas');
      }
      const canvas = canvasRef.current;
      if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctxRef.current = canvas.getContext('2d');
      }
      const ctx = ctxRef.current;
      if (!ctx) return null;
      ctx.drawImage(video, 0, 0);

      return faceLandmarkerRef.current.detectForVideo(canvas, Math.round(timestamp));
    } catch (err) {
      // Log first occurrence for debugging, then suppress
      if (!detect._errorLogged) {
        console.warn('Face detection error:', err.message || err);
        detect._errorLogged = true;
      }
      return null;
    }
  }, []);

  return { isLoading, loadProgress, error, detect };
}
